import { useState, useCallback } from "react";
import { PutObjectCommand, GetObjectCommand } from "@aws-sdk/client-s3";
import {
  StartTranscriptionJobCommand,
  GetTranscriptionJobCommand,
  TranscriptionJobStatus,
} from "@aws-sdk/client-transcribe";
import {
  createS3Client,
  createTranscribeClient,
} from "../utilities/awsChinaClients";
import { getAWSChinaConfig } from "../utilities/awsChinaConfig";
import { v4 as uuidv4 } from "uuid";

export const useAwsChinaTranscribe = () => {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcription, setTranscription] = useState<string | null>(null);

  const transcribe = useCallback(
    async (audioFile: File, languageCode: "en-US" | "zh-CN" = "en-US") => {
      setIsTranscribing(true);
      setError(null);
      setTranscription(null);

      try {
        const s3Client = createS3Client();
        const transcribeClient = createTranscribeClient();
        const config = getAWSChinaConfig();

        const uniqueJobName = `transcription-${uuidv4()}`;
        const s3Key = `audio-uploads/${uniqueJobName}/${audioFile.name}`;

        const arrayBuffer = await audioFile.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        await s3Client.send(
          new PutObjectCommand({
            Bucket: config.s3Bucket,
            Key: s3Key,
            Body: uint8Array,
            ContentType: audioFile.type,
          })
        );

        const mediaFileUri = `s3://${config.s3Bucket}/${s3Key}`;
        await transcribeClient.send(
          new StartTranscriptionJobCommand({
            TranscriptionJobName: uniqueJobName,
            LanguageCode: languageCode,
            Media: {
              MediaFileUri: mediaFileUri,
            },
            OutputBucketName: config.s3Bucket,
          })
        );

        let jobStatus: TranscriptionJobStatus | undefined;
        let jobDetails;

        while (jobStatus !== "COMPLETED") {
          await new Promise((resolve) => setTimeout(resolve, 1000));

          jobDetails = await transcribeClient.send(
            new GetTranscriptionJobCommand({
              TranscriptionJobName: uniqueJobName,
            })
          );

          jobStatus = jobDetails.TranscriptionJob?.TranscriptionJobStatus;
          console.log("jobStatus", jobStatus);

          if (jobStatus === "FAILED") {
            throw new Error(
              jobDetails.TranscriptionJob?.FailureReason ||
                "Transcription job failed."
            );
          }
        }

        const transcriptKey = `${uniqueJobName}.json`;
        const { Body } = await s3Client.send(
          new GetObjectCommand({
            Bucket: config.s3Bucket,
            Key: transcriptKey,
          })
        );

        if (!Body) {
          throw new Error("Transcript file has no body.");
        }

        const resultString = await Body.transformToString();
        const resultJson = JSON.parse(resultString);

        const transcriptText = resultJson.results.transcripts[0].transcript;

        setTranscription(transcriptText);
        setIsTranscribing(false);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "An unknown error occurred during transcription.";
        setError(errorMessage);
        console.error("Transcription failed:", err);
      } finally {
        setIsTranscribing(false);
      }
    },
    []
  );

  return { transcribe, isTranscribing, error, transcription };
};
